import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getApiUrl } from '../../utils/apiUrl';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { FiSave, FiUpload, FiRefreshCw, FiExternalLink, FiLoader, FiPlay, FiTrash2, FiImage, FiFolder } from 'react-icons/fi';
import RichTextEditor from '../../components/ui/RichTextEditor';

const API_URL = getApiUrl();

// IMPORTANT: Le slug doit correspondre à la page dans la BD qui est AFFICHÉE sur le site
const PAGES = [
  { slug: 'accueil', name: 'Accueil', previewPath: '/' },
  { slug: 'photobooth-presentation', name: 'PhotoBooth', previewPath: '/photobooth-presentation' },
  { slug: 'livre-dor-presentation', name: 'Livre d\'Or', previewPath: '/livre-dor-presentation' },
  { slug: 'videobooth-360-presentation', name: 'VideoBooth 360°', previewPath: '/videobooth-360-presentation' },
];

// Common emoji icons for hotspots
const HOTSPOT_ICONS = ['📷', '📹', '🎥', '📱', '💡', '⚡', '🔋', '🎨', '🎤', '⭐', '✨', '🎵', '🖨️', '🖥️', '🔄', '☀️'];

// Image Source Picker Modal Component
function ImageSourcePicker({ isOpen, onClose, onSelectMediaLibrary, onSelectUpload }) {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gray-800 rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl border border-gray-700" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-white mb-4 text-center">Choisir une image</h3>
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={onSelectMediaLibrary}
            className="flex flex-col items-center justify-center p-6 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors border border-gray-600 hover:border-amber-500"
          >
            <FiFolder className="w-10 h-10 text-amber-500 mb-3" />
            <span className="text-white font-medium">Médiathèque</span>
            <span className="text-gray-400 text-xs mt-1">Images existantes</span>
          </button>
          <button
            onClick={onSelectUpload}
            className="flex flex-col items-center justify-center p-6 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors border border-gray-600 hover:border-amber-500"
          >
            <FiUpload className="w-10 h-10 text-amber-500 mb-3" />
            <span className="text-white font-medium">Charger</span>
            <span className="text-gray-400 text-xs mt-1">Nouvelle image</span>
          </button>
        </div>
        <button
          onClick={onClose}
          className="mt-4 w-full py-2 text-gray-400 hover:text-white text-sm"
        >
          Annuler
        </button>
      </div>
    </div>
  );
}

// Media Library Browser Modal
function MediaLibraryBrowser({ isOpen, onClose, onSelectImage }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [brokenImages, setBrokenImages] = useState({});
  
  useEffect(() => {
    if (isOpen) {
      loadImages();
      setBrokenImages({});
    }
  }, [isOpen]);
  
  const loadImages = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/media?limit=200`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // API returns 'media' array with items having file_name, original_name, url
        const mediaItems = data.media || data.items || [];
        setImages(mediaItems);
        console.log('Loaded media:', mediaItems.length);
      }
    } catch (error) {
      console.error('Error loading media:', error);
    }
    setLoading(false);
  };

  // Check if URL is likely to be broken (external URLs that are known to be down)
  const isLikelyBroken = (url) => {
    if (!url) return true;
    return url.includes('justasmile-events.com') || url.includes('2026.justasmile');
  };

  // Get display URL (for rendering)
  const getDisplayUrl = (img) => {
    // Prefer local API URLs
    if (img.url && img.url.startsWith('/api')) {
      return `${API_URL}${img.url}`;
    }
    // External URLs that work
    if (img.url && img.url.startsWith('http') && !isLikelyBroken(img.url)) {
      return img.url;
    }
    // Fallback to file_name based URL if available
    if (img.file_name && img.user_id) {
      return `${API_URL}/api/media/file/${img.user_id}/${img.file_name}`;
    }
    return null;
  };

  // Get storage URL (for saving to DB)
  const getStorageUrl = (img) => {
    if (img.url && img.url.startsWith('/api')) {
      return img.url;
    }
    if (img.file_name && img.user_id) {
      return `/api/media/file/${img.user_id}/${img.file_name}`;
    }
    return img.url || '';
  };
  
  // Filter images: search + exclude likely broken ones
  const filteredImages = images.filter(img => {
    // Skip images with broken external URLs and no local alternative
    const displayUrl = getDisplayUrl(img);
    if (!displayUrl) return false;
    
    // Search filter
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      return img.file_name?.toLowerCase().includes(search) ||
             img.original_name?.toLowerCase().includes(search) ||
             img.name?.toLowerCase().includes(search);
    }
    return true;
  });

  // Further filter out images that failed to load
  const workingImages = filteredImages.filter((_, idx) => !brokenImages[idx]);
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gray-800 rounded-xl max-w-4xl w-full mx-4 max-h-[80vh] flex flex-col shadow-2xl border border-gray-700" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Médiathèque ({workingImages.length} images)</h3>
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="bg-gray-700 text-white px-3 py-1.5 rounded-lg text-sm w-48"
          />
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <FiLoader className="w-8 h-8 text-amber-500 animate-spin" />
            </div>
          ) : workingImages.length === 0 ? (
            <p className="text-gray-400 text-center py-12">Aucune image disponible</p>
          ) : (
            <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
              {workingImages.map((img, idx) => {
                const displayUrl = getDisplayUrl(img);
                const storageUrl = getStorageUrl(img);
                
                if (!displayUrl) return null;
                
                return (
                  <button
                    key={img.id || img.file_name || idx}
                    onClick={() => {
                      onSelectImage(storageUrl);
                      onClose();
                    }}
                    className="aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-amber-500 transition-colors bg-gray-700"
                    title={img.original_name || img.name || img.file_name}
                  >
                    <img
                      src={displayUrl}
                      alt={img.original_name || img.name || img.file_name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={() => setBrokenImages(prev => ({ ...prev, [idx]: true }))}
                    />
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="p-4 border-t border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-400 hover:text-white"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ConstructorPage() {
  const [selectedPage, setSelectedPage] = useState('accueil');
  const [siteData, setSiteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewKey, setPreviewKey] = useState(Date.now());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null);
  const previewRefreshTimeoutRef = useRef(null);
  const [selectedHotspotIndex, setSelectedHotspotIndex] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  
  // Image picker states
  const [showImageSourcePicker, setShowImageSourcePicker] = useState(false);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [pendingImageTarget, setPendingImageTarget] = useState(null);
  
  // Ref to always have latest siteData in auto-save
  const siteDataRef = useRef(siteData);
  const selectedPageRef = useRef(selectedPage);
  
  useEffect(() => {
    siteDataRef.current = siteData;
  }, [siteData]);
  
  useEffect(() => {
    selectedPageRef.current = selectedPage;
  }, [selectedPage]);

  // Auto-save and refresh preview after 2 seconds of inactivity
  const schedulePreviewRefresh = useCallback(() => {
    if (previewRefreshTimeoutRef.current) {
      clearTimeout(previewRefreshTimeoutRef.current);
    }
    setHasUnsavedChanges(true);
    
    // Auto-save after 2 seconds of inactivity for live preview
    previewRefreshTimeoutRef.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        // Use refs to get latest data
        const currentSiteData = siteDataRef.current;
        const currentPage = selectedPageRef.current;
        const pageData = currentSiteData?.pages?.find(p => p.slug === currentPage);
        
        if (!pageData || !token) {
          console.log('Auto-save skipped: no pageData or token');
          return;
        }
        
        console.log('Auto-saving page:', currentPage, 'sections:', pageData.sections.length);
        
        // Auto-save directly (is_draft: false) for live preview
        const response = await fetch(`${API_URL}/api/my-site/page/auto-save`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            page_slug: currentPage,
            sections: pageData.sections,
            is_draft: false  // Save directly to sections for live preview
          })
        });
        
        if (response.ok) {
          // Refresh preview iframe
          setPreviewKey(Date.now());
          setHasUnsavedChanges(false); // Mark as saved
          console.log('Auto-save successful');
        } else {
          console.log('Auto-save failed:', response.status);
        }
      } catch (error) {
        console.log('Auto-save error:', error);
      }
    }, 2000);
  }, []); // No dependencies needed since we use refs

  // Manual save only - no more auto-save to avoid scroll issues
  useEffect(() => {
    return () => {
      if (previewRefreshTimeoutRef.current) {
        clearTimeout(previewRefreshTimeoutRef.current);
      }
    };
  }, []);

  const getPageData = () => {
    return siteData?.pages?.find(p => p.slug === selectedPage);
  };

  useEffect(() => {
    loadSiteData();
  }, []);

  const loadSiteData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/public/site`);
      if (response.ok) {
        const data = await response.json();
        setSiteData(data);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const updateSection = (sectionIndex, field, value) => {
    if (!siteData) return;
    
    const updatedPages = siteData.pages.map(page => {
      if (page.slug === selectedPage) {
        const updatedSections = [...page.sections];
        updatedSections[sectionIndex] = {
          ...updatedSections[sectionIndex],
          content: {
            ...updatedSections[sectionIndex].content,
            [field]: value
          }
        };
        return { ...page, sections: updatedSections };
      }
      return page;
    });
    
    setSiteData({ ...siteData, pages: updatedPages });
    setHasUnsavedChanges(true);
    schedulePreviewRefresh();
  };

  const saveAndRefresh = async () => {
    if (!siteData) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const pageData = getPageData();
      
      if (!pageData) {
        toast.error('Page non trouvée');
        setSaving(false);
        return;
      }
      
      // Use the correct API endpoint for updating a page
      const response = await fetch(`${API_URL}/api/my-site/page`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          page_slug: selectedPage,
          sections: pageData.sections
        })
      });

      if (response.ok) {
        // Also sync hotspots to the global hotspots config if this page has a hotspots section
        const hotspotsSection = pageData.sections?.find(s => s.type === 'hotspots');
        if (hotspotsSection && hotspotsSection.items?.length > 0) {
          // Map page slug to hotspots config key
          let hotspotsKey = selectedPage;
          if (selectedPage === 'livre-dor' || selectedPage === 'livre-dor-presentation') {
            hotspotsKey = 'livre-dor-video';
          } else if (selectedPage === 'videobooth-360-presentation') {
            hotspotsKey = 'videobooth-360';
          }
          
          // Convert Constructor format to PreviewPage format
          const hotspotsData = {
            image: hotspotsSection.machineImage || '',
            title: hotspotsSection.title || 'Découvrez notre équipement',
            hotspots: hotspotsSection.items.map((item, idx) => ({
              id: idx + 1,
              x: item.left || 50,
              y: item.top || 50,
              label: item.label || 'Point',
              description: item.tooltip || '',
              icon: item.icon || '✨'
            }))
          };
          
          // Fetch existing config and merge
          try {
            const configRes = await fetch(`${API_URL}/api/hotspots-config`);
            let existingConfig = {};
            if (configRes.ok) {
              existingConfig = await configRes.json() || {};
            }
            
            // Update config with new hotspots for this page
            existingConfig[hotspotsKey] = hotspotsData;
            
            // Save updated config
            await fetch(`${API_URL}/api/hotspots-config`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify(existingConfig)
            });
          } catch (err) {
            console.log('Could not sync hotspots config:', err);
          }
        }
        
        toast.success('Sauvegardé !');
        setPreviewKey(Date.now()); // Refresh preview
        // Reload data to get updated version
        await loadSiteData();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Erreur de sauvegarde: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      toast.error('Aucun fichier sélectionné');
      return;
    }
    if (!uploadTarget) {
      toast.error('Cible d\'upload non définie');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Non authentifié - veuillez vous reconnecter');
      }
      
      const response = await fetch(`${API_URL}/api/media/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        const imageUrl = data.url || data.file_url;
        
        if (!imageUrl) {
          throw new Error('URL de l\'image non reçue du serveur');
        }
        
        // Handle upload for nested items (like services carousel or gallery images)
        if (uploadTarget.itemIndex !== undefined) {
          // Get current page data directly from state
          const currentPageData = siteData?.pages?.find(p => p.slug === selectedPage);
          if (!currentPageData) {
            throw new Error('Page courante non trouvée');
          }
          
          const section = currentPageData.sections?.[uploadTarget.idx];
          if (!section) {
            throw new Error(`Section ${uploadTarget.idx} non trouvée`);
          }
          
          // Determine which array to update (items or images)
          const arrayField = uploadTarget.field || 'items';
          const currentArray = section.content?.[arrayField] || [];
          const newArray = [...currentArray];
          
          if (uploadTarget.itemIndex >= 0 && uploadTarget.itemIndex < newArray.length) {
            newArray[uploadTarget.itemIndex] = {
              ...newArray[uploadTarget.itemIndex],
              [uploadTarget.itemField]: imageUrl
            };
            updateSection(uploadTarget.idx, arrayField, newArray);
          } else {
            throw new Error(`Index d'item invalide: ${uploadTarget.itemIndex}`);
          }
        } else {
          updateSection(uploadTarget.idx, uploadTarget.field, imageUrl);
        }
        toast.success('Image uploadée avec succès');
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Erreur serveur (${response.status})`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur: ' + error.message);
    }
    
    setUploadTarget(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const pageData = getPageData();
  const currentPageConfig = PAGES.find(p => p.slug === selectedPage);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <FiLoader className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        className="hidden"
      />

      {/* Header avec onglets de pages */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="flex items-center justify-between px-4 py-2">
          <div className="flex items-center gap-1">
            {PAGES.map(page => (
              <button
                key={page.slug}
                onClick={() => setSelectedPage(page.slug)}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  selectedPage === page.slug
                    ? 'bg-amber-500 text-black'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {page.name}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={loadSiteData} className="border-gray-600">
              <FiRefreshCw className="w-4 h-4" />
            </Button>
            <Button onClick={saveAndRefresh} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black">
              {saving ? <FiLoader className="w-4 h-4 animate-spin mr-1" /> : <FiSave className="w-4 h-4 mr-1" />}
              Sauvegarder
            </Button>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="flex" style={{ height: 'calc(100vh - 57px)' }}>
        {/* Éditeur - gauche */}
        <div className="w-[450px] overflow-y-auto p-4 space-y-4 bg-gray-800/30">
          {pageData?.sections?.map((section, idx) => (
            <div key={idx} className="bg-gray-800 rounded-lg p-4 space-y-3">
              <div className="text-amber-500 font-semibold text-sm uppercase tracking-wide border-b border-gray-700 pb-2">
                {getSectionLabel(section.type)}
              </div>
              
              {renderFields(section, idx, updateSection, (fieldOrTarget) => {
                // Handle both simple field string and complex target object
                if (typeof fieldOrTarget === 'string') {
                  setPendingImageTarget({ idx, field: fieldOrTarget });
                } else {
                  // For carousel items with itemIndex and itemField
                  setPendingImageTarget({ idx, ...fieldOrTarget });
                }
                // Show image source picker instead of directly opening file input
                setShowImageSourcePicker(true);
              }, selectedHotspotIndex, setSelectedHotspotIndex, isDragging, setIsDragging)}
            </div>
          ))}
          
          {(!pageData?.sections || pageData.sections.length === 0) && (
            <div className="text-center py-8 text-gray-500">
              Aucune section sur cette page
            </div>
          )}
        </div>

        {/* Aperçu - droite */}
        <div className="flex-1 bg-black">
          <div className="bg-gray-800 px-3 py-2 flex items-center justify-between border-b border-gray-700">
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-400">Aperçu</span>
              {hasUnsavedChanges && (
                <span className="text-xs text-amber-500 animate-pulse">● Modifications en attente...</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setPreviewKey(Date.now());
                  toast.success('Aperçu actualisé');
                }}
                className="text-xs text-gray-400 hover:text-white flex items-center gap-1 transition-colors"
                title="Actualiser l'aperçu"
              >
                <FiRefreshCw className="w-3 h-3" /> Actualiser
              </button>
              <a
                href={currentPageConfig?.previewPath}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-amber-500 hover:underline flex items-center gap-1"
              >
                <FiExternalLink className="w-3 h-3" /> Ouvrir
              </a>
            </div>
          </div>
          
          {/* Auto-refresh notice */}
          <div className="bg-green-500/10 border-b border-green-500/30 px-4 py-1.5 flex items-center justify-center gap-2">
            <span className="text-green-400 text-xs">✨ L'aperçu se met à jour automatiquement après 2 secondes d'inactivité</span>
          </div>
          
          {/* Video preview notice */}
          <div className="bg-amber-500/10 border-b border-amber-500/30 px-4 py-2 flex items-center justify-center gap-2">
            <FiPlay className="w-4 h-4 text-amber-500" />
            <span className="text-amber-500 text-xs">Les vidéos YouTube ne s'affichent pas en aperçu. Cliquez sur "Ouvrir" pour voir la page complète.</span>
          </div>
          
          <iframe
            key={previewKey}
            src={`${currentPageConfig?.previewPath}?_t=${previewKey}`}
            className="w-full border-0"
            style={{ height: 'calc(100vh - 97px)' }}
            title="Aperçu"
          />
        </div>
      </div>
      
      {/* Image Source Picker Modal */}
      <ImageSourcePicker
        isOpen={showImageSourcePicker}
        onClose={() => {
          setShowImageSourcePicker(false);
          setPendingImageTarget(null);
        }}
        onSelectMediaLibrary={() => {
          setShowImageSourcePicker(false);
          setShowMediaLibrary(true);
        }}
        onSelectUpload={() => {
          setShowImageSourcePicker(false);
          if (pendingImageTarget) {
            setUploadTarget(pendingImageTarget);
          }
          fileInputRef.current?.click();
        }}
      />
      
      {/* Media Library Browser Modal */}
      <MediaLibraryBrowser
        isOpen={showMediaLibrary}
        onClose={() => {
          setShowMediaLibrary(false);
          setPendingImageTarget(null);
        }}
        onSelectImage={(imageUrl) => {
          if (pendingImageTarget && siteData) {
            const { idx, field, itemIndex, itemField } = pendingImageTarget;
            
            // Handle different field structures
            if (itemIndex !== undefined && field === 'images') {
              // Gallery images array (gallery or presentation_gallery sections)
              const pageData = getPageData();
              const section = pageData?.sections?.[idx];
              const currentImages = section?.content?.images;
              
              // Ensure we have an array
              const images = Array.isArray(currentImages) ? [...currentImages] : [];
              
              // Ensure the array has enough elements
              while (images.length <= itemIndex) {
                images.push({ url: '', alt: `Image ${images.length + 1}`, positionX: 50, positionY: 50, scale: 100 });
              }
              
              // Update the specific image
              if (typeof images[itemIndex] === 'string') {
                images[itemIndex] = { url: imageUrl, alt: `Image ${itemIndex + 1}`, positionX: 50, positionY: 50, scale: 100 };
              } else {
                images[itemIndex] = { ...images[itemIndex], url: imageUrl };
              }
              updateSection(idx, 'images', images);
            } else if (itemIndex !== undefined && itemField && field !== 'images') {
              // Carousel or nested items (but NOT images field which is handled above)
              const pageData = getPageData();
              const section = pageData?.sections?.[idx];
              const items = [...(section?.content?.[field] || [])];
              items[itemIndex] = { ...items[itemIndex], [itemField]: imageUrl };
              updateSection(idx, field, items);
            } else {
              // Simple field
              updateSection(idx, field, imageUrl);
            }
            
            toast.success('Image sélectionnée');
          }
          setPendingImageTarget(null);
        }}
      />
    </div>
  );
}

function getSectionLabel(type) {
  const labels = {
    'hero': 'Hero',
    'hero_video': 'Hero Vidéo',
    'text_image': 'Texte & Image',
    'features_icons': 'Fonctionnalités incluses',
    'services_carousel': 'Nos Prestations (Carrousel)',
    'about': 'À propos',
    'events_marquee': 'Événements',
    'stats': 'Statistiques',
    'partners_logos': 'Partenaires',
    'cta': 'Appel à l\'action',
    'faq': 'FAQ',
    'hotspots': 'Points Chauds (Machine)',
    'reviews': 'Avis Clients',
    'models': 'Modèles / Galerie',
    'models_gallery': 'Galerie Modèles',
    'gallery': 'Galerie Photos (Accueil)',
    'presentation_gallery': 'Galerie Photos (Présentation)',
  };
  return labels[type] || type;
}

function renderFields(section, idx, updateSection, triggerUpload, selectedHotspotIndex, setSelectedHotspotIndex, isDragging, setIsDragging) {
  const c = section.content || {};
  const t = section.type;
  
  return (
    <div className="space-y-3">
      {/* Titre */}
      {c.title !== undefined && t !== 'gallery' && t !== 'models' && t !== 'models_gallery' && (
        <Field label="Titre" value={c.title} onChange={v => updateSection(idx, 'title', v)} />
      )}
      
      {/* Sous-titre */}
      {c.subtitle !== undefined && (
        <Field label="Sous-titre" value={c.subtitle} onChange={v => updateSection(idx, 'subtitle', v)} />
      )}
      
      {/* Badge/Bandeau texte défilant */}
      {t === 'hero_video' && (
        <Field 
          label="Bandeau texte (défilant)" 
          value={c.badge || ''} 
          onChange={v => updateSection(idx, 'badge', v)} 
        />
      )}
      
      {/* Texte */}
      {c.text !== undefined && (
        <Field label="Texte" value={c.text} onChange={v => updateSection(idx, 'text', v)} multiline />
      )}
      
      {/* Description */}
      {c.description !== undefined && t !== 'models' && t !== 'models_gallery' && (
        <Field label="Description" value={c.description} onChange={v => updateSection(idx, 'description', v)} multiline />
      )}
      
      {/* Texte en avant */}
      {c.highlightText !== undefined && (
        <Field label="Texte en avant" value={c.highlightText} onChange={v => updateSection(idx, 'highlightText', v)} />
      )}
      
      {/* URL Vidéo */}
      {(c.videoUrl !== undefined || t === 'hero_video' || t === 'text_image') && (
        <Field label={t === 'text_image' ? "URL Vidéo YouTube (présentation)" : "URL Vidéo YouTube (fond)"} value={c.videoUrl || ''} onChange={v => updateSection(idx, 'videoUrl', v)} placeholder="https://www.youtube.com/watch?v=..." />
      )}
      
      {/* URL Vidéo Installation (bulle) */}
      {t === 'hero_video' && (
        <Field label="URL Vidéo Installation (bulle)" value={c.installationVideoUrl || ''} onChange={v => updateSection(idx, 'installationVideoUrl', v)} placeholder="https://www.youtube.com/watch?v=..." />
      )}
      
      {/* Bouton */}
      {c.buttonText !== undefined && t !== 'models' && t !== 'models_gallery' && (
        <Field label="Texte du bouton" value={c.buttonText} onChange={v => updateSection(idx, 'buttonText', v)} />
      )}
      
      {/* Image */}
      {c.image !== undefined && (
        <ImageField 
          label="Image" 
          value={c.image} 
          onChange={v => updateSection(idx, 'image', v)} 
          onUpload={() => triggerUpload('image')}
        />
      )}
      
      {/* Background */}
      {c.backgroundImage !== undefined && (
        <ImageField 
          label="Image de fond" 
          value={c.backgroundImage} 
          onChange={v => updateSection(idx, 'backgroundImage', v)} 
          onUpload={() => triggerUpload('backgroundImage')}
        />
      )}
      
      {/* Position image */}
      {c.imagePosition !== undefined && (
        <div>
          <Label className="text-xs text-gray-400">Position image</Label>
          <select
            value={c.imagePosition || 'right'}
            onChange={e => updateSection(idx, 'imagePosition', e.target.value)}
            className="w-full mt-1 bg-gray-700 border-gray-600 text-white rounded px-2 py-1.5 text-sm"
          >
            <option value="left">Gauche</option>
            <option value="right">Droite</option>
          </select>
        </div>
      )}
      
      {/* Overlay */}
      {c.overlayOpacity !== undefined && (
        <div>
          <Label className="text-xs text-gray-400">Opacité overlay ({Math.round((c.overlayOpacity || 0.5) * 100)}%)</Label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={c.overlayOpacity || 0.5}
            onChange={e => updateSection(idx, 'overlayOpacity', parseFloat(e.target.value))}
            className="w-full mt-1"
          />
        </div>
      )}
      
      {/* Stats Editor - with value and label */}
      {t === 'stats' && c.items && Array.isArray(c.items) && (
        <div className="space-y-4">
          {/* Hero Stats (affichées en haut du site) */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs text-gray-400">Stats du Hero (en haut du site)</Label>
              <button
                onClick={() => {
                  const newHeroStats = [...(c.heroStats || []), { icon: 'star', value: '+0', label: 'nouveau' }];
                  updateSection(idx, 'heroStats', newHeroStats);
                }}
                className="text-xs bg-amber-500 text-black px-2 py-1 rounded hover:bg-amber-600"
              >
                + Ajouter
              </button>
            </div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {(c.heroStats || []).map((item, i) => (
                <div key={i} className="bg-gray-700/50 rounded p-2 flex gap-2 items-center">
                  <select
                    value={item.icon || 'star'}
                    onChange={e => {
                      const newHeroStats = [...(c.heroStats || [])];
                      newHeroStats[i] = { ...item, icon: e.target.value };
                      updateSection(idx, 'heroStats', newHeroStats);
                    }}
                    className="w-20 bg-gray-600 text-white rounded px-1 py-1 text-xs"
                  >
                    <option value="star">⭐ Star</option>
                    <option value="heart">❤️ Coeur</option>
                    <option value="calendar">📅 Calendrier</option>
                    <option value="check">✓ Check</option>
                    <option value="award">🏆 Award</option>
                  </select>
                  <input
                    value={item.value || ''}
                    onChange={e => {
                      const newHeroStats = [...(c.heroStats || [])];
                      newHeroStats[i] = { ...item, value: e.target.value };
                      updateSection(idx, 'heroStats', newHeroStats);
                    }}
                    className="w-20 bg-gray-600 text-white rounded px-2 py-1 text-sm text-center font-bold"
                    placeholder="+500"
                  />
                  <input
                    value={item.label || ''}
                    onChange={e => {
                      const newHeroStats = [...(c.heroStats || [])];
                      newHeroStats[i] = { ...item, label: e.target.value };
                      updateSection(idx, 'heroStats', newHeroStats);
                    }}
                    className="flex-1 bg-gray-600 text-white rounded px-2 py-1 text-sm"
                    placeholder="événements"
                  />
                  <button
                    onClick={() => {
                      const newHeroStats = (c.heroStats || []).filter((_, index) => index !== i);
                      updateSection(idx, 'heroStats', newHeroStats);
                    }}
                    className="text-red-400 hover:text-red-300"
                  >
                    ✕
                  </button>
                </div>
              ))}
              {(!c.heroStats || c.heroStats.length === 0) && (
                <p className="text-gray-500 text-xs italic">Aucune stat hero. Cliquez "+ Ajouter" pour créer.</p>
              )}
            </div>
          </div>
          
          {/* Main Stats */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs text-gray-400">Statistiques principales</Label>
              <button
                onClick={() => {
                  const newItems = [...(c.items || []), { value: '0', label: 'Nouvelle stat' }];
                  updateSection(idx, 'items', newItems);
                }}
                className="text-xs bg-amber-500 text-black px-2 py-1 rounded hover:bg-amber-600"
              >
                + Ajouter
              </button>
            </div>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {c.items.map((item, i) => (
                <div key={i} className="bg-gray-700/50 rounded p-2 flex gap-2 items-center">
                  <input
                    value={item.value || ''}
                    onChange={e => {
                      const newItems = [...c.items];
                      newItems[i] = { ...item, value: e.target.value };
                      updateSection(idx, 'items', newItems);
                    }}
                    className="w-24 bg-gray-600 text-white rounded px-2 py-1 text-sm text-center font-bold"
                    placeholder="Valeur"
                  />
                  <input
                    value={item.label || ''}
                    onChange={e => {
                      const newItems = [...c.items];
                      newItems[i] = { ...item, label: e.target.value };
                      updateSection(idx, 'items', newItems);
                    }}
                    className="flex-1 bg-gray-600 text-white rounded px-2 py-1 text-sm"
                    placeholder="Label"
                  />
                  <button
                    onClick={() => {
                      const newItems = c.items.filter((_, index) => index !== i);
                      updateSection(idx, 'items', newItems);
                    }}
                    className="text-red-400 hover:text-red-300"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Generic Items - for services, features, etc. */}
      {c.items && Array.isArray(c.items) && c.items.length > 0 && t !== 'faq' && t !== 'hotspots' && t !== 'stats' && t !== 'services_carousel' && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs text-gray-400">Éléments</Label>
            <button
              onClick={() => {
                const newItems = [...(c.items || []), { title: 'Nouvel élément' }];
                updateSection(idx, 'items', newItems);
              }}
              className="text-xs bg-amber-500 text-black px-2 py-1 rounded hover:bg-amber-600"
            >
              + Ajouter
            </button>
          </div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {c.items.map((item, i) => (
              <div key={i} className="flex gap-1">
                <input
                  value={item.title || item.label || item.name || item.text || ''}
                  onChange={e => {
                    const newItems = [...c.items];
                    const key = item.title !== undefined ? 'title' : item.label !== undefined ? 'label' : item.name !== undefined ? 'name' : 'text';
                    newItems[i] = { ...item, [key]: e.target.value };
                    updateSection(idx, 'items', newItems);
                  }}
                  className="flex-1 bg-gray-700 border-gray-600 text-white rounded px-2 py-1 text-sm"
                  placeholder={`Élément ${i + 1}`}
                />
                <button
                  onClick={() => {
                    const newItems = c.items.filter((_, index) => index !== i);
                    updateSection(idx, 'items', newItems);
                  }}
                  className="text-red-400 hover:text-red-300 px-1"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Services Carousel Editor - with images for each card */}
      {t === 'services_carousel' && c.items && (
        <div>
          <Label className="text-xs text-gray-400 mb-2 block">Cartes Prestations</Label>
          <div className="space-y-4 max-h-[500px] overflow-y-auto">
            {c.items.map((item, i) => (
              <div key={i} className="bg-gray-700/50 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-amber-500 font-semibold text-sm">{item.title || `Carte ${i + 1}`}</span>
                  <button
                    onClick={() => {
                      const newItems = c.items.filter((_, index) => index !== i);
                      updateSection(idx, 'items', newItems);
                    }}
                    className="text-red-400 hover:text-red-300 text-xs"
                  >
                    ✕ Supprimer
                  </button>
                </div>
                
                {/* Titre */}
                <div>
                  <label className="text-xs text-gray-400">Titre</label>
                  <input
                    value={item.title || ''}
                    onChange={e => {
                      const newItems = [...c.items];
                      newItems[i] = { ...item, title: e.target.value };
                      updateSection(idx, 'items', newItems);
                    }}
                    className="w-full bg-gray-600 text-white rounded px-2 py-1 text-sm mt-1"
                    placeholder="Titre de la carte"
                  />
                </div>
                
                {/* Description */}
                <div>
                  <label className="text-xs text-gray-400">Description</label>
                  <div className="mt-1">
                    <RichTextEditor
                      value={item.description || ''}
                      onChange={v => {
                        const newItems = [...c.items];
                        newItems[i] = { ...item, description: v };
                        updateSection(idx, 'items', newItems);
                      }}
                      minHeight="60px"
                      maxHeight="120px"
                      simple={false}
                    />
                  </div>
                </div>
                
                {/* Image de fond */}
                <div>
                  <label className="text-xs text-gray-400">Image de fond</label>
                  <div className="flex gap-1 mt-1">
                    <input
                      value={item.image || ''}
                      onChange={e => {
                        const newItems = [...c.items];
                        newItems[i] = { ...item, image: e.target.value };
                        updateSection(idx, 'items', newItems);
                      }}
                      className="flex-1 bg-gray-600 text-white rounded px-2 py-1 text-sm"
                      placeholder="URL de l'image"
                    />
                    <button
                      onClick={() => {
                        triggerUpload({ field: 'items', itemIndex: i, itemField: 'image' });
                      }}
                      className="bg-gray-500 text-white px-2 py-1 rounded text-sm hover:bg-gray-400"
                    >
                      📤
                    </button>
                  </div>
                  {item.image && (
                    <img 
                      src={item.image.startsWith('http') ? item.image : `${API_URL}${item.image}`}
                      alt=""
                      className="mt-2 h-20 rounded object-cover"
                      onError={e => e.target.style.display = 'none'}
                    />
                  )}
                </div>
                
                {/* Lien */}
                <div>
                  <label className="text-xs text-gray-400">Lien (slug)</label>
                  <input
                    value={item.link || ''}
                    onChange={e => {
                      const newItems = [...c.items];
                      newItems[i] = { ...item, link: e.target.value };
                      updateSection(idx, 'items', newItems);
                    }}
                    className="w-full bg-gray-600 text-white rounded px-2 py-1 text-sm mt-1"
                    placeholder="ex: photobooth"
                  />
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={() => {
              const newItems = [...(c.items || []), { 
                title: 'Nouvelle prestation', 
                description: 'Description...', 
                image: '',
                link: ''
              }];
              updateSection(idx, 'items', newItems);
            }}
            className="mt-2 w-full text-xs bg-amber-500 text-black px-2 py-2 rounded hover:bg-amber-600"
          >
            + Ajouter une prestation
          </button>
        </div>
      )}

      {/* FAQ Editor */}
      {t === 'faq' && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs text-gray-400">Questions / Réponses</Label>
            <button
              onClick={() => {
                const newItems = [...(c.items || []), { question: 'Nouvelle question', answer: 'Réponse...' }];
                updateSection(idx, 'items', newItems);
              }}
              className="text-xs bg-amber-500 text-black px-2 py-1 rounded hover:bg-amber-600"
            >
              + Ajouter
            </button>
          </div>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {(c.items || []).map((item, i) => (
              <div key={i} className="bg-gray-700/50 rounded p-2 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-amber-500 text-xs font-bold">Q{i + 1}</span>
                  <input
                    value={item.question || ''}
                    onChange={e => {
                      const newItems = [...c.items];
                      newItems[i] = { ...item, question: e.target.value };
                      updateSection(idx, 'items', newItems);
                    }}
                    className="flex-1 bg-gray-600 border-gray-500 text-white rounded px-2 py-1 text-sm"
                    placeholder="Question"
                  />
                  <button
                    onClick={() => {
                      const newItems = c.items.filter((_, index) => index !== i);
                      updateSection(idx, 'items', newItems);
                    }}
                    className="text-red-400 hover:text-red-300 text-xs"
                  >
                    ✕
                  </button>
                </div>
                <RichTextEditor
                  value={item.answer || ''}
                  onChange={v => {
                    const newItems = [...c.items];
                    newItems[i] = { ...item, answer: v };
                    updateSection(idx, 'items', newItems);
                  }}
                  minHeight="60px"
                  maxHeight="120px"
                  simple={false}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Models Section Editor - Modèles personnalisés */}
      {(t === 'models' || t === 'models_gallery') && (
        <div className="space-y-4">
          {/* Titre */}
          <div>
            <Label className="text-xs text-gray-400">Titre de la section</Label>
            <input
              value={c.title || ''}
              onChange={e => updateSection(idx, 'title', e.target.value)}
              className="w-full bg-gray-700 border-gray-600 text-white rounded px-2 py-1 text-sm mt-1"
              placeholder="Des modèles personnalisés pour un résultat unique !"
            />
          </div>
          
          {/* Description */}
          <div>
            <Label className="text-xs text-gray-400">Description</Label>
            <div className="mt-1">
              <RichTextEditor
                value={c.description || ''}
                onChange={v => updateSection(idx, 'description', v)}
                minHeight="80px"
                maxHeight="150px"
                simple={false}
              />
            </div>
          </div>
          
          {/* Texte du bouton */}
          <div>
            <Label className="text-xs text-gray-400">Texte du bouton</Label>
            <input
              value={c.buttonText || ''}
              onChange={e => updateSection(idx, 'buttonText', e.target.value)}
              className="w-full bg-gray-700 border-gray-600 text-white rounded px-2 py-1 text-sm mt-1"
              placeholder="Découvrir nos modèles"
            />
          </div>
          
          {/* Lien du bouton */}
          <div>
            <Label className="text-xs text-gray-400">Lien du bouton</Label>
            <input
              value={c.buttonLink || ''}
              onChange={e => updateSection(idx, 'buttonLink', e.target.value)}
              className="w-full bg-gray-700 border-gray-600 text-white rounded px-2 py-1 text-sm mt-1"
              placeholder="modeles-photobooth"
            />
          </div>
          
          {/* Images du carousel */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs text-gray-400">Images du carousel ({(c.images || []).length})</Label>
              <button
                onClick={() => {
                  const newImages = [...(c.images || []), { url: '', alt: `Modèle ${(c.images || []).length + 1}` }];
                  updateSection(idx, 'images', newImages);
                }}
                className="text-xs bg-amber-500 text-black px-2 py-1 rounded hover:bg-amber-600"
              >
                + Ajouter
              </button>
            </div>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {(c.images || []).map((img, i) => {
                const imgUrl = typeof img === 'string' ? img : img.url || img.src;
                return (
                  <div key={i} className="bg-gray-700/50 rounded p-2 flex gap-2 items-center">
                    <span className="text-amber-500 text-xs font-bold">#{i + 1}</span>
                    <input
                      value={imgUrl || ''}
                      onChange={e => {
                        const newImages = [...(c.images || [])];
                        newImages[i] = typeof img === 'string' 
                          ? e.target.value 
                          : { ...img, url: e.target.value };
                        updateSection(idx, 'images', newImages);
                      }}
                      className="flex-1 bg-gray-600 text-white rounded px-2 py-1 text-xs"
                      placeholder="URL de l'image"
                    />
                    <button
                      onClick={() => triggerUpload({ field: 'images', itemIndex: i })}
                      className="text-amber-500 hover:text-amber-400 p-1"
                    >
                      <FiUpload className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => {
                        const newImages = (c.images || []).filter((_, index) => index !== i);
                        updateSection(idx, 'images', newImages);
                      }}
                      className="text-red-400 hover:text-red-300 text-xs"
                    >
                      ✕
                    </button>
                    {imgUrl && (
                      <img 
                        src={imgUrl.startsWith('http') ? imgUrl : `${API_URL}${imgUrl}`}
                        alt=""
                        className="w-8 h-8 rounded object-cover"
                        onError={e => e.target.style.display = 'none'}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Hotspots Editor */}
      {t === 'hotspots' && (
        <div>
          {/* Description text for hotspots section */}
          <div className="mb-3">
            <Label className="text-xs text-gray-400">Description (texte à côté de l'image)</Label>
            <p className="text-xs text-amber-500/70 mb-1">💡 Utilisez ✨ pour l'effet doré animé</p>
            <RichTextEditor
              value={c.description || ''}
              onChange={v => updateSection(idx, 'description', v)}
              placeholder="Il s'agit d'une borne à selfie qui permet de capturer des souvenirs..."
              minHeight="80px"
              maxHeight="150px"
              simple={false}
            />
          </div>
          
          {/* Second paragraph */}
          <div className="mb-3">
            <Label className="text-xs text-gray-400">Paragraphe supplémentaire (optionnel)</Label>
            <RichTextEditor
              value={c.description2 || ''}
              onChange={v => updateSection(idx, 'description2', v)}
              placeholder="Chaque invité peut repartir avec une ou plusieurs impressions..."
              minHeight="60px"
              maxHeight="150px"
              simple={false}
            />
          </div>
          
          {/* Machine Image */}
          <div className="mb-3">
            <Label className="text-xs text-gray-400">Image de la machine</Label>
            <div className="flex gap-1 mt-1">
              <input
                value={c.machineImage || ''}
                onChange={e => updateSection(idx, 'machineImage', e.target.value)}
                className="flex-1 bg-gray-700 border-gray-600 text-white rounded px-2 py-1 text-sm"
                placeholder="URL de l'image"
              />
              <button
                onClick={() => triggerUpload('machineImage')}
                className="bg-gray-600 text-white px-2 py-1 rounded text-sm"
              >
                📤
              </button>
            </div>
          </div>

          {/* LIVE PREVIEW - Visual representation of hotspots on machine image */}
          {c.machineImage && (
            <div className="mb-4 bg-gray-900 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs text-amber-400">📍 Prévisualisation en direct</Label>
                <div className="flex items-center gap-2">
                  {selectedHotspotIndex !== null && (
                    <span className="text-[10px] text-green-400 bg-green-900/30 px-2 py-0.5 rounded">
                      Point #{selectedHotspotIndex + 1} sélectionné
                    </span>
                  )}
                  <p className="text-[10px] text-gray-500">Cliquez sur l'image pour positionner</p>
                </div>
              </div>
              <div 
                className={`relative w-full cursor-crosshair transition-all ${isDragging ? 'ring-2 ring-amber-500' : ''}`}
                style={{ paddingBottom: '120%' }}
                onClick={(e) => {
                  // Position the selected hotspot (or the last one if none selected)
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = Math.round(((e.clientX - rect.left) / rect.width) * 100);
                  const y = Math.round(((e.clientY - rect.top) / rect.height) * 100);
                  
                  if (c.items && c.items.length > 0) {
                    const newItems = [...c.items];
                    const targetIdx = selectedHotspotIndex !== null ? selectedHotspotIndex : newItems.length - 1;
                    if (targetIdx >= 0 && targetIdx < newItems.length) {
                      newItems[targetIdx] = { ...newItems[targetIdx], left: x, top: y };
                      updateSection(idx, 'items', newItems);
                      toast.success(`Point #${targetIdx + 1} repositionné à ${x}%, ${y}%`);
                    }
                  }
                }}
              >
                <img
                  src={c.machineImage.startsWith('http') ? c.machineImage : `${API_URL}${c.machineImage}`}
                  alt="Machine preview"
                  className="absolute inset-0 w-full h-full object-contain rounded pointer-events-none"
                  onError={e => e.target.style.display = 'none'}
                />
                {/* Hotspot markers overlay */}
                {(c.items || []).map((item, i) => (
                  <div
                    key={i}
                    className={`absolute transform -translate-x-1/2 -translate-y-1/2 cursor-move group transition-all duration-200 ${
                      selectedHotspotIndex === i ? 'z-20 scale-125' : 'z-10'
                    }`}
                    style={{ 
                      left: `${item.left || 50}%`, 
                      top: `${item.top || 50}%`
                    }}
                    draggable
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedHotspotIndex(selectedHotspotIndex === i ? null : i);
                    }}
                    onDragStart={() => setIsDragging(true)}
                    onDragEnd={(e) => {
                      setIsDragging(false);
                      const rect = e.currentTarget.parentElement.getBoundingClientRect();
                      const x = Math.round(Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100)));
                      const y = Math.round(Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100)));
                      const newItems = [...c.items];
                      newItems[i] = { ...newItems[i], left: x, top: y };
                      updateSection(idx, 'items', newItems);
                    }}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shadow-lg border-2 transition-all ${
                      selectedHotspotIndex === i 
                        ? 'bg-green-500 border-green-300 text-white ring-4 ring-green-500/50 animate-pulse' 
                        : 'bg-amber-500 border-white text-black hover:scale-110'
                    }`}>
                      {item.icon || (i + 1)}
                    </div>
                    {/* Tooltip on hover */}
                    <div className="absolute left-10 top-0 bg-black/95 text-white text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-30 max-w-48">
                      <div className="font-medium">{item.label || 'Sans nom'}</div>
                      <div className="text-gray-400 text-[10px]">Position: {item.left || 50}%, {item.top || 50}%</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-2">
                <p className="text-[10px] text-green-400">
                  ✨ Cliquez sur un point pour le sélectionner, puis cliquez sur l'image pour le repositionner
                </p>
                {selectedHotspotIndex !== null && (
                  <button 
                    onClick={() => setSelectedHotspotIndex(null)}
                    className="text-[10px] text-gray-400 hover:text-white"
                  >
                    Désélectionner
                  </button>
                )}
              </div>
            </div>
          )}
          
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs text-gray-400">Points interactifs ({(c.items || []).length})</Label>
            <button
              onClick={() => {
                const newItems = [...(c.items || []), { 
                  id: Date.now().toString(),
                  label: 'Nouveau point', 
                  tooltip: 'Description...', 
                  left: 50, 
                  top: 50,
                  icon: '✨'
                }];
                updateSection(idx, 'items', newItems);
                setSelectedHotspotIndex(newItems.length - 1); // Auto-select the new hotspot
              }}
              className="text-xs bg-amber-500 text-black px-2 py-1 rounded hover:bg-amber-600"
            >
              + Ajouter
            </button>
          </div>
          <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
            {(c.items || []).map((item, i) => (
              <div 
                key={i} 
                className={`rounded-lg p-3 space-y-3 border transition-all cursor-pointer ${
                  selectedHotspotIndex === i 
                    ? 'bg-green-900/30 border-green-500 ring-2 ring-green-500/30' 
                    : 'bg-gray-700/50 border-gray-600 hover:border-gray-500'
                }`}
                onClick={() => setSelectedHotspotIndex(selectedHotspotIndex === i ? null : i)}
              >
                <div className="flex items-center gap-2">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 transition-colors ${
                    selectedHotspotIndex === i ? 'bg-green-500 text-white' : 'bg-amber-500 text-black'
                  }`}>{item.icon || (i + 1)}</span>
                  <input
                    value={item.label || ''}
                    onChange={e => {
                      const newItems = [...c.items];
                      newItems[i] = { ...item, label: e.target.value };
                      updateSection(idx, 'items', newItems);
                    }}
                    onClick={e => e.stopPropagation()}
                    className="flex-1 bg-gray-600 text-white rounded px-2 py-1.5 text-sm font-medium"
                    placeholder="Nom du point"
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const newItems = c.items.filter((_, index) => index !== i);
                      updateSection(idx, 'items', newItems);
                      if (selectedHotspotIndex === i) setSelectedHotspotIndex(null);
                    }}
                    className="text-red-400 hover:text-red-300 p-1"
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Icon selector */}
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-gray-400">Icône:</span>
                  <div className="flex flex-wrap gap-1">
                    {HOTSPOT_ICONS.map(icon => (
                      <button
                        key={icon}
                        onClick={(e) => {
                          e.stopPropagation();
                          const newItems = [...c.items];
                          newItems[i] = { ...item, icon };
                          updateSection(idx, 'items', newItems);
                        }}
                        className={`w-6 h-6 rounded text-sm hover:bg-gray-500 transition-colors ${
                          item.icon === icon ? 'bg-amber-500' : 'bg-gray-600'
                        }`}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>
                
                <input
                  value={item.tooltip || ''}
                  onChange={e => {
                    const newItems = [...c.items];
                    newItems[i] = { ...item, tooltip: e.target.value };
                    updateSection(idx, 'items', newItems);
                  }}
                  onClick={e => e.stopPropagation()}
                  className="w-full bg-gray-600 text-white rounded px-2 py-1.5 text-sm"
                  placeholder="Description au survol"
                />
                
                {/* Position controls - Compact inline layout */}
                <div className="bg-gray-800/50 rounded p-2">
                  <div className="flex gap-4 items-center">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] text-gray-400">X (horizontal)</span>
                        <span className="text-xs text-amber-400 font-mono bg-gray-700 px-1.5 rounded">{item.left || 50}%</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={item.left || 50}
                        onChange={e => {
                          const newItems = [...c.items];
                          newItems[i] = { ...item, left: parseInt(e.target.value) || 0 };
                          updateSection(idx, 'items', newItems);
                        }}
                        className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-amber-500"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] text-gray-400">Y (vertical)</span>
                        <span className="text-xs text-amber-400 font-mono bg-gray-700 px-1.5 rounded">{item.top || 50}%</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={item.top || 50}
                        onChange={e => {
                          const newItems = [...c.items];
                          newItems[i] = { ...item, top: parseInt(e.target.value) || 0 };
                          updateSection(idx, 'items', newItems);
                        }}
                        className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-amber-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Events Marquee Editor */}
      {t === 'events_marquee' && (
        <div className="space-y-4">
          {/* Event Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs text-gray-400">Types d'événements</Label>
              <button
                onClick={() => {
                  const newItems = [...(c.items || []), { name: 'Nouvel événement', image: '' }];
                  updateSection(idx, 'items', newItems);
                }}
                className="text-xs bg-amber-500 text-black px-2 py-1 rounded hover:bg-amber-600"
              >
                + Ajouter
              </button>
            </div>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {(c.items || []).map((item, i) => (
                <div key={i} className="bg-gray-700/50 rounded p-2">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-amber-500 text-xs font-bold">#{i + 1}</span>
                    <input
                      value={item.name || ''}
                      onChange={e => {
                        const newItems = [...c.items];
                        newItems[i] = { ...item, name: e.target.value };
                        updateSection(idx, 'items', newItems);
                      }}
                      className="flex-1 bg-gray-600 text-white rounded px-2 py-1 text-sm"
                      placeholder="Nom de l'événement (ex: Mariage)"
                    />
                    <button
                      onClick={() => {
                        const newItems = c.items.filter((_, index) => index !== i);
                        updateSection(idx, 'items', newItems);
                      }}
                      className="text-red-400 hover:text-red-300 text-xs"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="flex gap-1">
                    <input
                      value={item.image || ''}
                      onChange={e => {
                        const newItems = [...c.items];
                        newItems[i] = { ...item, image: e.target.value };
                        updateSection(idx, 'items', newItems);
                      }}
                      className="flex-1 bg-gray-600 text-white rounded px-2 py-1 text-sm"
                      placeholder="URL de l'image"
                    />
                    <button
                      onClick={() => triggerUpload({ field: 'items', itemIndex: i, itemField: 'image' })}
                      className="bg-gray-500 text-white px-2 py-1 rounded text-sm hover:bg-gray-400"
                    >
                      📤
                    </button>
                  </div>
                  {item.image && (
                    <img 
                      src={item.image.startsWith('http') ? item.image : `${API_URL}${item.image}`}
                      alt={item.name}
                      className="mt-2 h-16 w-16 rounded object-cover"
                      onError={e => e.target.style.display = 'none'}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Gallery Editor (Homepage) */}
      {t === 'gallery' && (
        <div className="space-y-4">
          {/* Title parts */}
          <div>
            <Label className="text-xs text-gray-400">Début du titre</Label>
            <Input
              value={c.title || ''}
              onChange={e => updateSection(idx, 'title', e.target.value)}
              className="mt-1 bg-gray-700 border-gray-600 text-white text-sm"
              placeholder="Just a Smile Events vous propose la location de"
            />
          </div>
          
          <div>
            <Label className="text-xs text-gray-400">Services animés (séparés par virgule)</Label>
            <Input
              value={(c.highlightedServices || []).join(', ')}
              onChange={e => updateSection(idx, 'highlightedServices', e.target.value.split(',').map(s => s.trim()))}
              className="mt-1 bg-gray-700 border-gray-600 text-white text-sm"
              placeholder="PhotoBooth, VideoBooth 360, Livre d'Or Vidéo"
            />
          </div>
          
          <div>
            <Label className="text-xs text-gray-400">Fin du titre</Label>
            <Input
              value={c.titleSuffix || ''}
              onChange={e => updateSection(idx, 'titleSuffix', e.target.value)}
              className="mt-1 bg-gray-700 border-gray-600 text-white text-sm"
              placeholder="pour immortaliser vos événements !"
            />
          </div>
          
          {/* Gallery Images */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs text-gray-400">Images de la galerie (9 max)</Label>
              {(c.images || []).length < 9 && (
                <button
                  onClick={() => {
                    const newImages = [...(c.images || []), { url: '', alt: `Image ${(c.images || []).length + 1}` }];
                    updateSection(idx, 'images', newImages);
                  }}
                  className="text-xs bg-amber-500 text-black px-2 py-1 rounded hover:bg-amber-600"
                >
                  + Ajouter
                </button>
              )}
            </div>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {(c.images || []).map((img, i) => (
                <div key={i} className="bg-gray-700/50 rounded p-2">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-amber-500 text-xs font-bold">#{i + 1}</span>
                    <input
                      value={img.alt || ''}
                      onChange={e => {
                        const newImages = [...c.images];
                        newImages[i] = { ...img, alt: e.target.value };
                        updateSection(idx, 'images', newImages);
                      }}
                      className="flex-1 bg-gray-600 text-white rounded px-2 py-1 text-sm"
                      placeholder="Description"
                    />
                    <button
                      onClick={() => {
                        const newImages = c.images.filter((_, index) => index !== i);
                        updateSection(idx, 'images', newImages);
                      }}
                      className="text-red-400 hover:text-red-300 text-xs"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="flex gap-1">
                    <input
                      value={img.url || ''}
                      onChange={e => {
                        const newImages = [...c.images];
                        newImages[i] = { ...img, url: e.target.value };
                        updateSection(idx, 'images', newImages);
                      }}
                      className="flex-1 bg-gray-600 text-white rounded px-2 py-1 text-sm"
                      placeholder="URL de l'image"
                    />
                    <button
                      onClick={() => triggerUpload({ field: 'images', itemIndex: i, itemField: 'url' })}
                      className="bg-gray-500 text-white px-2 py-1 rounded text-sm hover:bg-gray-400"
                    >
                      📤
                    </button>
                  </div>
                  {img.url && (
                    <div className="mt-2">
                      <img 
                        src={img.url.startsWith('http') ? img.url : `${API_URL}${img.url}`}
                        alt={img.alt}
                        className="h-16 rounded object-cover w-full"
                        style={{ objectPosition: `${img.positionX ?? 50}% ${img.positionY ?? 50}%` }}
                        onError={e => e.target.style.display = 'none'}
                      />
                      <div className="flex gap-2 mt-1">
                        <div className="flex-1">
                          <label className="text-[10px] text-gray-500">Position X</label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={img.positionX ?? 50}
                            onChange={e => {
                              const newImages = [...c.images];
                              newImages[i] = { ...img, positionX: parseInt(e.target.value) };
                              updateSection(idx, 'images', newImages);
                            }}
                            className="w-full h-1 accent-amber-500"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="text-[10px] text-gray-500">Position Y</label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={img.positionY ?? 50}
                            onChange={e => {
                              const newImages = [...c.images];
                              newImages[i] = { ...img, positionY: parseInt(e.target.value) };
                              updateSection(idx, 'images', newImages);
                            }}
                            className="w-full h-1 accent-amber-500"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Presentation Gallery Editor (Product pages) */}
      {t === 'presentation_gallery' && (
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs text-gray-400">Images de la galerie (4 recommandées)</Label>
              {(c.images || []).length < 8 && (
                <button
                  onClick={() => {
                    const newImages = [...(c.images || []), { url: '', alt: `Image ${(c.images || []).length + 1}` }];
                    updateSection(idx, 'images', newImages);
                  }}
                  className="text-xs bg-amber-500 text-black px-2 py-1 rounded hover:bg-amber-600"
                >
                  + Ajouter
                </button>
              )}
            </div>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {(c.images || []).map((img, i) => {
                const imgUrl = typeof img === 'string' ? img : (img.url || '');
                const imgAlt = typeof img === 'string' ? `Image ${i + 1}` : (img.alt || `Image ${i + 1}`);
                return (
                  <div key={i} className="bg-gray-700/50 rounded p-2">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-amber-500 text-xs font-bold">#{i + 1}</span>
                      <input
                        value={imgAlt}
                        onChange={e => {
                          const newImages = [...(c.images || [])];
                          newImages[i] = typeof img === 'string' 
                            ? { url: img, alt: e.target.value }
                            : { ...img, alt: e.target.value };
                          updateSection(idx, 'images', newImages);
                        }}
                        placeholder="Description"
                        className="flex-1 text-xs bg-gray-600 border-0 rounded px-2 py-1 text-white"
                      />
                      <button
                        onClick={() => {
                          const newImages = (c.images || []).filter((_, j) => j !== i);
                          updateSection(idx, 'images', newImages);
                        }}
                        className="text-red-400 hover:text-red-300 p-1"
                      >
                        <FiTrash2 className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="flex gap-1">
                      <input
                        value={imgUrl}
                        onChange={e => {
                          const newImages = [...(c.images || [])];
                          newImages[i] = typeof img === 'string'
                            ? { url: e.target.value, alt: imgAlt }
                            : { ...img, url: e.target.value };
                          updateSection(idx, 'images', newImages);
                        }}
                        placeholder="URL de l'image"
                        className="flex-1 text-xs bg-gray-600 border-0 rounded px-2 py-1 text-white"
                      />
                      <button
                        onClick={() => triggerUpload({ field: 'images', itemIndex: i })}
                        className="text-amber-500 hover:text-amber-400 p-1"
                      >
                        <FiUpload className="w-3 h-3" />
                      </button>
                    </div>
                    {imgUrl && (
                      <div className="mt-2">
                        <div className="relative overflow-hidden rounded h-20">
                          <img
                            src={imgUrl.startsWith('http') ? imgUrl : `${API_URL}${imgUrl}`}
                            alt={imgAlt}
                            className="absolute inset-0 w-full h-full object-cover"
                            style={{ 
                              objectPosition: `${(typeof img === 'object' ? img.positionX : 50) ?? 50}% ${(typeof img === 'object' ? img.positionY : 50) ?? 50}%`,
                              transform: `scale(${(typeof img === 'object' ? img.scale : 100) ?? 100}%)`,
                              transformOrigin: `${(typeof img === 'object' ? img.positionX : 50) ?? 50}% ${(typeof img === 'object' ? img.positionY : 50) ?? 50}%`
                            }}
                            onError={e => e.target.style.display = 'none'}
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-2 mt-1">
                          <div>
                            <label className="text-[10px] text-gray-500">Position X</label>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={(typeof img === 'object' ? img.positionX : 50) ?? 50}
                              onChange={e => {
                                const newImages = [...(c.images || [])];
                                newImages[i] = typeof img === 'string'
                                  ? { url: img, alt: imgAlt, positionX: parseInt(e.target.value), positionY: 50, scale: 100 }
                                  : { ...img, positionX: parseInt(e.target.value) };
                                updateSection(idx, 'images', newImages);
                              }}
                              className="w-full h-1 accent-amber-500"
                            />
                          </div>
                          <div>
                            <label className="text-[10px] text-gray-500">Position Y</label>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={(typeof img === 'object' ? img.positionY : 50) ?? 50}
                              onChange={e => {
                                const newImages = [...(c.images || [])];
                                newImages[i] = typeof img === 'string'
                                  ? { url: img, alt: imgAlt, positionX: 50, positionY: parseInt(e.target.value), scale: 100 }
                                  : { ...img, positionY: parseInt(e.target.value) };
                                updateSection(idx, 'images', newImages);
                              }}
                              className="w-full h-1 accent-amber-500"
                            />
                          </div>
                          <div>
                            <label className="text-[10px] text-gray-500">Zoom ({(typeof img === 'object' ? img.scale : 100) ?? 100}%)</label>
                            <input
                              type="range"
                              min="100"
                              max="200"
                              step="5"
                              value={(typeof img === 'object' ? img.scale : 100) ?? 100}
                              onChange={e => {
                                const newImages = [...(c.images || [])];
                                newImages[i] = typeof img === 'string'
                                  ? { url: img, alt: imgAlt, positionX: 50, positionY: 50, scale: parseInt(e.target.value) }
                                  : { ...img, scale: parseInt(e.target.value) };
                                updateSection(idx, 'images', newImages);
                              }}
                              className="w-full h-1 accent-amber-500"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {(!c.images || c.images.length === 0) && (
                <p className="text-gray-500 text-xs text-center py-4">
                  Aucune image. Cliquez sur "+ Ajouter" pour commencer.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, multiline, richText = true }) {
  return (
    <div>
      <Label className="text-xs text-gray-400">{label}</Label>
      {multiline ? (
        richText ? (
          <div className="mt-1">
            <RichTextEditor
              value={value || ''}
              onChange={onChange}
              minHeight="80px"
              maxHeight="200px"
              simple={false}
            />
          </div>
        ) : (
          <>
            <Textarea
              value={value || ''}
              onChange={e => onChange(e.target.value)}
              className="mt-1 bg-gray-700 border-gray-600 text-white text-sm min-h-[80px]"
            />
            <p className="text-[10px] text-amber-500/70 mt-1">
              💡 Astuce : Utilisez <code className="bg-gray-700 px-1 rounded">{"{{texte}}"}</code> pour mettre en doré
            </p>
          </>
        )
      ) : (
        <Input
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          className="mt-1 bg-gray-700 border-gray-600 text-white text-sm h-8"
        />
      )}
    </div>
  );
}

function ImageField({ label, value, onChange, onUpload }) {
  return (
    <div>
      <Label className="text-xs text-gray-400">{label}</Label>
      <div className="flex gap-1 mt-1">
        <Input
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          className="flex-1 bg-gray-700 border-gray-600 text-white text-sm h-8"
          placeholder="URL"
        />
        <Button variant="outline" size="sm" onClick={onUpload} className="h-8 px-2 border-gray-600">
          <FiUpload className="w-3 h-3" />
        </Button>
      </div>
      {value && (
        <img 
          src={value.startsWith('http') ? value : `${API_URL}${value}`}
          alt=""
          className="mt-1 h-16 rounded object-cover"
          onError={e => e.target.style.display = 'none'}
        />
      )}
    </div>
  );
}
